/**
 * The force-action outcome vocabulary — extracted from ForceActionDialog.tsx (rev 17b) so
 * UnlinkOnlyDialog and the bulk-result renderer (BulkForceDialog.tsx) import ONE definition instead of
 * forking it. A second hand-copied copy of this map is exactly the drift
 * feedback_union_from_array_not_handwritten.md warns about — every action key the force/bulk-force
 * pipeline can emit lives here, and only here.
 *
 * r14 22/7 (principal feedback, first live force): the raw action keys ("ok_license_only") are pipeline
 * vocabulary — an operator needs a sentence in the pill's own words. Every key the force/bulk-force path
 * can emit maps to copy; an unmapped key still renders raw (accurate beats pretty, same rule as the audit
 * formatter).
 *
 * rev 17b: `skip_has_open_period` is the ONE new code the bulk-force endpoint can emit that force-license-
 * remove/force-unlink never could (a period-holder must go through PeriodModal's own force buttons — see
 * the Manage-every-row matrix in rev17b-addendum.md).
 */
import type { TKey } from "@/i18n/keys";

export const OUTCOME_KEY: Record<string, TKey> = {
  ok_license_only: "pool.period.force.outcome.okLicenseRemoved",
  ok_unlink: "pool.period.force.outcome.okUnlinked",
  dry_run_license_only: "pool.period.force.outcome.dryRunLicenseRemoved",
  dry_run_unlink: "pool.period.force.outcome.dryRunUnlinked",
  ok_already_basic: "pool.period.force.outcome.alreadyBasic",
  dry_run_already_basic: "pool.period.force.outcome.alreadyBasic",
  abort_404: "pool.period.force.outcome.abort404",
  abort_account_mismatch: "pool.period.force.outcome.abortGuard",
  abort_live_role: "pool.period.force.outcome.abortGuard",
  abort_live_zoom_one: "pool.period.force.outcome.abortGuard",
  abort_live_phone: "pool.period.force.outcome.abortGuard",
  abort_get_failed: "pool.period.force.outcome.abortGetFailed",
  failed_patch: "pool.period.force.outcome.failedWrite",
  failed_unlink: "pool.period.force.outcome.failedWrite",
  skip_no_membership: "pool.period.force.outcome.skipGuard",
  skip_role_id: "pool.period.force.outcome.skipGuard",
  skip_pool_guard: "pool.period.force.outcome.skipGuard",
  skip_standalone_unsupported: "pool.period.force.outcome.skipGuard",
  skip_standalone_unreachable: "pool.period.force.outcome.skipGuard",
  skip_concurrent_intent: "pool.period.force.outcome.skipGuard",
  // the mirror-tripwire family (master accounts) — no_mirror/stale legs get their own actionable
  // copy: the common bootstrap case is "user created today, mirror lands tomorrow ~07:05" (คุณใหม่
  // hit exactly this on the first master force, 22/7 18:23)
  skip_tripwire_no_mirror_row: "pool.period.force.outcome.skipMirrorNotReady",
  skip_tripwire_stale_null_fetched_at: "pool.period.force.outcome.skipMirrorNotReady",
  skip_tripwire_stale_row: "pool.period.force.outcome.skipMirrorNotReady",
  skip_tripwire_fetch_status: "pool.period.force.outcome.skipMirrorNotReady",
  skip_tripwire_zoom_phone: "pool.period.force.outcome.skipGuard",
  skip_tripwire_zoom_one: "pool.period.force.outcome.skipGuard",
  // rev 17b (bulk-force only): a period-holder hit the membership-keyed bulk path — refused, because
  // acting on it would strip the license while leaving the period open (a split record). Their path is
  // the per-row Manage button (PeriodModal's own force buttons), not bulk.
  skip_has_open_period: "pool.period.force.outcome.skipHasOpenPeriod",
  // R1 late ruling (rev17b addendum, post-write read-back verification): the Zoom write itself was
  // accepted, but the targeted GET-to-verify (immediate + one ~3s retry) still saw the old state —
  // the sweep will confirm it on its own next pass. NOT a failure and NOT a guaranteed no-op like the
  // skip_*/abort_* family above — informational, so isPendingSweepCode() below routes it away from
  // both the "ok" and "failed" tones every result panel in this module uses.
  accepted_pending_sweep: "pool.period.force.outcome.acceptedPendingSweep",
  // Set-period-only (BulkSetPeriodDialog): these two are SYNTHESIZED client-side per email — the
  // /pool/periods endpoint is one-request-per-email and returns a 201 or a 4xx, never a force-style
  // outcome code, so the bulk-set-period run loop mints these itself. They are NOT emitted by the
  // force/bulk-force pipeline. Prefix-keyed like everything else here: `ok_created` → bucket ok / rank 2,
  // `failed_create` → bucket failed / rank 0.
  ok_created: "pool.period.force.outcome.okCreated",
  failed_create: "pool.period.force.outcome.failedCreate",
};

/** Sort rank for the expanded per-email breakdown: "failed_" worst (0), then "abort_"/"skip_" (1), then
 *  "ok_"/"dry_run_" (2) — an operator scanning the list sees problems first. Shared by BulkForceDialog
 *  and BulkSetPeriodDialog so the two result panels can never disagree on ordering. */
export function rank(code: string): number {
  if (code.startsWith("failed_")) return 0;
  if (code.startsWith("ok_") || code.startsWith("dry_run_")) return 2;
  return 1; // skip_* / abort_*
}

/** Tone-bucket for the summary counts ("N succeeded · N skipped · N failed"). Prefix-keyed so a new
 *  `ok_`/`failed_`/`skip_` code buckets automatically. Shared by BulkForceDialog and BulkSetPeriodDialog. */
export function bucket(code: string): "ok" | "skip" | "failed" {
  if (code.startsWith("failed_")) return "failed";
  if (code.startsWith("ok_") || code.startsWith("dry_run_")) return "ok";
  return "skip";
}

/** "ok_" / "dry_run_" codes render clean (the sentence alone); every guard/failure class keeps the raw
 *  key alongside the sentence — support conversations need the exact wire code. */
export function outcomeText(action: string, t: (k: TKey) => string): string {
  const key = OUTCOME_KEY[action];
  if (!key) return action;
  const sentence = t(key);
  return action.startsWith("ok_") || action.startsWith("dry_run_")
    ? sentence
    : `${sentence} (${action})`;
}

/** True for the "a real Zoom write landed" family — the summary/badge tone split every result panel in
 *  this module uses (ForceActionDialog, UnlinkOnlyDialog, BulkForceDialog). */
export function isOkCode(code: string): boolean {
  return code.startsWith("ok_");
}

/** R1 late ruling — true ONLY for `accepted_pending_sweep`: the write was accepted but the immediate
 *  read-back verification couldn't confirm it landed yet, so the sweep will close the loop. Neither
 *  `isOkCode` (verified) nor a failure — a caller uses this to route the code to an "info" tone instead
 *  of collapsing it into "ok" or the generic skip/neutral bucket. */
export function isPendingSweepCode(code: string): boolean {
  return code === "accepted_pending_sweep";
}
